import { useMemo } from "react";
import { DirectorySort } from "@common/constants/counterparty";
import { RegisteredCounterpartyIncludeScope } from "@common/helpers/counterparties";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { InvoiceExtractionRecord } from "@common/schemas/financialDocument/invoice-extraction-record.schema";
import { useCounterpartiesQuery } from "@api/counterparty.api";
import { Loader } from "@design-system/Loader/Loader";
import { useEntities } from "@hooks/useEntities";
import { useEntitiesContextsQueryState } from "@hooks/useEntitiesContexts";
import { useScope } from "@state/scopeContext.lib";
import { AccrualDocumentImporterMode } from "../accrualDocumentImporter.lib";
import { AccrualImporterDocumentRow } from "../accrualImporter.types";
import { accrualDraftCopy } from "./accrualDraftCopy";
import { AccrualDraftDetailsForm } from "./AccrualDraftDetailsForm";
import { AccrualDraftDrawerShell } from "./AccrualDraftDrawerShell";
import { matchExtractedCounterparty } from "./matchExtractedCounterparty";

export const AccrualDraftDetailsWorkspace: React.FC<{
    financialDocument: FinancialDocumentExtended;
    extraction: InvoiceExtractionRecord | null;
    mode: AccrualDocumentImporterMode;
    navigationRows: AccrualImporterDocumentRow[];
    onClose: () => void;
    onDiscard: () => void;
    onImported: () => void;
}> = ({
    financialDocument,
    extraction,
    mode,
    navigationRows,
    onClose,
    onDiscard,
    onImported,
}) => {
    const copy = accrualDraftCopy(mode);
    const { workspaceId } = useScope();
    const entities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const entityQuery = useEntitiesContextsQueryState();
    const extractedCounterpartyName =
        extraction?.counterpartyId == null
            ? (extraction?.counterpartyName?.trim() ?? "")
            : "";
    const needsCustomerLookup =
        extraction?.counterpartyId != null || extractedCounterpartyName !== "";
    const counterpartiesQuery = useCounterpartiesQuery(
        {
            sort: DirectorySort.NAME_ASC,
            search: extractedCounterpartyName || undefined,
            includeRegistered: needsCustomerLookup
                ? RegisteredCounterpartyIncludeScope.ALL
                : RegisteredCounterpartyIncludeScope.USED,
        },
        { enabled: needsCustomerLookup },
    );
    const entitiesReady =
        entityQuery?.status === "success" || entityQuery?.status === "error";
    const customerReady =
        !needsCustomerLookup ||
        counterpartiesQuery.isSuccess ||
        counterpartiesQuery.isError;
    const entity = useMemo(() => {
        const matched = entities.find(
            (item) => item.id === extraction?.entityId,
        );
        if (matched) {
            return matched;
        }
        return null;
    }, [entities, extraction?.entityId]);
    const customer = useMemo(() => {
        const counterparties = (counterpartiesQuery.data?.data ?? []).map(
            (row) => row.counterparty,
        );
        const matchedById = counterparties.find(
            (counterparty) => counterparty.id === extraction?.counterpartyId,
        );
        if (matchedById) {
            return {
                id: matchedById.id,
                name: matchedById.name,
                email: matchedById.email,
                phoneNumber: null,
            };
        }
        if (!workspaceId || extractedCounterpartyName === "") {
            return null;
        }
        const matchedByName = matchExtractedCounterparty({
            name: extractedCounterpartyName,
            workspaceId,
            counterparties,
        });
        if (!matchedByName) {
            return null;
        }
        return {
            id: matchedByName.id,
            name: matchedByName.name,
            email: matchedByName.email,
            phoneNumber: null,
        };
    }, [
        counterpartiesQuery.data,
        extractedCounterpartyName,
        extraction?.counterpartyId,
        workspaceId,
    ]);
    const suggestedCustomerName =
        customer || extractedCounterpartyName === ""
            ? null
            : extractedCounterpartyName;

    if (!entitiesReady || !customerReady) {
        return (
            <AccrualDraftDrawerShell
                financialDocument={financialDocument}
                navigationRows={navigationRows}
                onClose={onClose}
            >
                <Loader />
            </AccrualDraftDrawerShell>
        );
    }

    if (entities.length === 0) {
        return (
            <AccrualDraftDrawerShell
                financialDocument={financialDocument}
                navigationRows={navigationRows}
                onClose={onClose}
            >
                <p>{copy.missingEntity}</p>
            </AccrualDraftDrawerShell>
        );
    }

    return (
        <AccrualDraftDetailsForm
            key={`${financialDocument.id}:${extraction?.id ?? "blank"}`}
            financialDocument={financialDocument}
            extraction={extraction}
            entities={entities}
            entity={entity}
            customer={customer}
            suggestedCustomerName={suggestedCustomerName}
            mode={mode}
            navigationRows={navigationRows}
            onClose={onClose}
            onDiscard={onDiscard}
            onImported={onImported}
        />
    );
};
